import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { ShieldWarning, CheckCircle, WarningCircle, CaretRight, Buildings, MapPin } from "@phosphor-icons/react";

function DocRow({ d }) {
  const nav = useNavigate();
  const done = d.acknowledged;
  return (
    <button data-testid={`myrams-doc-${d.id}`} onClick={() => nav(`/policies/${d.id}`)}
      className="w-full text-left bg-white border border-slate-200 rounded-lg px-4 py-3 flex items-center justify-between gap-3 hover:border-emerald-300 transition-colors">
      <div className="min-w-0">
        <div className="font-medium text-slate-800 text-sm truncate">{d.title}</div>
        <div className="text-xs text-slate-500 truncate">{d.ref}{d.revision ? ` · rev ${d.revision}` : ""} · {d.sources?.join(", ")}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {done ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 inline-flex items-center gap-1"><CheckCircle size={13} /> Acknowledged</span>
          : d.action_required ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 inline-flex items-center gap-1"><WarningCircle size={13} /> Read &amp; acknowledge</span>
          : <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{d.ack_state === "future_effective" ? "Opens soon" : "No action"}</span>}
        <CaretRight size={16} className="text-slate-300" />
      </div>
    </button>
  );
}

export default function MyRamsPage() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/policies/my/rams").then((r) => setData(r.data)).catch(() => toast.error("Failed to load your RAMS"));
  }, []);

  if (!data) return <div className="p-8 text-slate-400">Loading…</div>;
  const empty = data.clients.length === 0 && data.general.length === 0;

  return (
    <div className="max-w-3xl" data-testid="my-rams-page">
      <div className="mb-5">
        <h1 className="font-display text-3xl font-black tracking-tight text-slate-900 flex items-center gap-2"><ShieldWarning size={28} className="text-amber-600" /> My RAMS / Method Statements</h1>
        <p className="font-body text-slate-600 mt-1 text-sm">Documents that apply to you, grouped by client and job/site. Open one to read and acknowledge. Questions &amp; suggestions are on each document.</p>
      </div>
      <div className="flex gap-3 mb-5">
        <div className="rounded-lg bg-rose-50 border border-rose-200 px-4 py-2 text-sm text-rose-800" data-testid="myrams-count-required"><b>{data.counts.required}</b> to acknowledge</div>
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-2 text-sm text-emerald-800" data-testid="myrams-count-acked"><b>{data.counts.acknowledged}</b> acknowledged</div>
      </div>

      {empty && <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 text-center text-slate-500 text-sm">No RAMS or method statements currently apply to you.</div>}

      {data.clients.map((c) => (
        <div key={c.client} className="mb-6" data-testid={`myrams-client-${c.client}`}>
          <h2 className="font-semibold text-slate-800 flex items-center gap-1.5 mb-2"><Buildings size={18} className="text-slate-500" /> {c.client}</h2>
          {c.client_wide.length > 0 && (
            <div className="mb-3">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">All client work</div>
              <div className="flex flex-col gap-2">{c.client_wide.map((d) => <DocRow key={d.id} d={d} />)}</div>
            </div>
          )}
          {c.jobs.map((j) => (
            <div key={j.job} className="mb-3">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1"><MapPin size={13} /> {j.job}</div>
              <div className="flex flex-col gap-2">{j.documents.map((d) => <DocRow key={d.id} d={d} />)}</div>
            </div>
          ))}
        </div>
      ))}

      {data.general.length > 0 && (
        <div className="mb-6" data-testid="myrams-general">
          <h2 className="font-semibold text-slate-800 mb-2">General / company-wide</h2>
          <div className="flex flex-col gap-2">{data.general.map((d) => <DocRow key={d.id} d={d} />)}</div>
        </div>
      )}
    </div>
  );
}
